import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type MentionVolumeOverTimeView = {
  /** `Total mentions` (with its delta) and `Daily average`. */
  kpis: KPIMetric[];
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope — an empty scope is empty even if buckets exist. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type MentionVolumeOverTimeCardProps = WidgetBlockProps & {
  view?: MentionVolumeOverTimeView;
  /** Opens the conversations behind a point (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Mentions' first card (Figma 947:18142), and Overview's (972:19379).
 *
 * Counts, not reach: one post read by a million people is one mention here.
 * **No badge on the daily average, deliberately** — over a same-length
 * comparison period its % change is identical to the total's.
 */
export function MentionVolumeOverTimeCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: MentionVolumeOverTimeCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Mention volume over time"
      helpText="Every mention of the brand and its monitored keywords across all connected sources, day by day."
      helpSupportingText="Counts, not reach: one post read by a million people is one mention here. Read it with the sentiment cards to tell a busy day from a bad one."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
